import { Controller } from 'react-hook-form';
import {
  Autocomplete,
  DateInput,
  DateInputSize,
  IconLoader,
  InputSize,
  InputText,
  PermissionEnum,
  Select,
  SelectSize,
  SelectType,
} from '@ui/src';
import InfoRow from '../../common/info-row';
import { NewApplicationStatus } from '../../../../hoc/common/types';
import StepPartBlock from '../../common/step-part-block';
import { GeneralInfoTypes } from './types';

import commonStyles from '../../common/styles.module.css';
import cn from '../styles.module.css';

const GeneralInfo = ({
  control,
  clearErrors,
  activePermissions,
  status,
  errors,
  regulatedCompanyScheme,
  cofRegulatedCompanyScheme,
  questionnarieControl,
  questionnarieClearErrors,
  questionnarieErrors,
  countries,
  hasSumSubId,
  isEditable,
  questionnarieFields,
  applicationScheme,
  questionnarieIsLoading,
}: GeneralInfoTypes) => (
  <StepPartBlock title="General information" zIndex={30}>
    <InfoRow title="First Name" id="firstName" lessPadding>
      <Controller
        control={control}
        name="firstName"
        defaultValue=""
        render={({ field: { onChange, value } }) => (
          <InputText
            name="firstName"
            placeholder="Enter first name..."
            value={value}
            onChange={(e) => {
              onChange(e);
              clearErrors(['firstName']);
            }}
            size={InputSize.SMALL}
            className="borderless"
            error={errors?.firstName?.message}
            tabIndex={
              hasSumSubId ||
              !isEditable ||
              Boolean(
                status === NewApplicationStatus.ACCEPTED ||
                status === NewApplicationStatus.DECLINED,
              )
                ? -1
                : 0
            }
            disabled={
              hasSumSubId ||
              !isEditable ||
              Boolean(
                status === NewApplicationStatus.ACCEPTED ||
                status === NewApplicationStatus.DECLINED,
              )
            }
          />
        )}
      />
    </InfoRow>
    <InfoRow title="Last name" id="lastName" lessPadding>
      <Controller
        control={control}
        name="lastName"
        defaultValue=""
        render={({ field: { onChange, value } }) => (
          <InputText
            name="lastName"
            placeholder="Enter last name..."
            value={value}
            onChange={(e) => {
              onChange(e);
              clearErrors(['lastName']);
            }}
            size={InputSize.SMALL}
            className="borderless"
            error={errors?.lastName?.message}
            tabIndex={
              hasSumSubId ||
              !isEditable ||
              Boolean(
                status === NewApplicationStatus.ACCEPTED ||
                status === NewApplicationStatus.DECLINED,
              )
                ? -1
                : 0
            }
            disabled={
              hasSumSubId ||
              !isEditable ||
              Boolean(
                status === NewApplicationStatus.ACCEPTED ||
                status === NewApplicationStatus.DECLINED,
              )
            }
          />
        )}
      />
    </InfoRow>
    <InfoRow title="Date of birth" id="dob" lessPadding>
      <Controller
        control={control}
        name="dob"
        defaultValue=""
        render={({ field: { onChange, value } }) => {
          let className;

          if (
            status === NewApplicationStatus.ACCEPTED ||
            status === NewApplicationStatus.DECLINED
          ) {
            className = commonStyles.dateInputDisabled;

            if (value) {
              className = `${commonStyles.dateInputDisabled} ${
                value ? commonStyles.dateInputDisabledWithValue : ''
              }`;
            }
          }
          return (
            <DateInput
              value={value || ''}
              placeholder="Enter date of birth"
              onChange={(date) => {
                onChange(date.toISOString());
                clearErrors(['dob']);
              }}
              className={className}
              size={DateInputSize.SMALL}
              error={errors?.dob?.message}
              tabIndex={
                hasSumSubId ||
                !isEditable ||
                Boolean(
                  status === NewApplicationStatus.ACCEPTED ||
                  status === NewApplicationStatus.DECLINED,
                )
                  ? -1
                  : 0
              }
              disabled={
                hasSumSubId ||
                !isEditable ||
                Boolean(
                  status === NewApplicationStatus.ACCEPTED ||
                  status === NewApplicationStatus.DECLINED,
                )
              }
              birthdayPicker
              borderless
              withoutCustomSelector
            />
          );
        }}
      />
    </InfoRow>

    {(regulatedCompanyScheme || cofRegulatedCompanyScheme) && (
      <InfoRow
        title="Place of birth"
        id="birthCountryId"
        style={{
          position: 'relative',
          zIndex: 19,
        }}
        lessPadding
      >
        <div className={cn.placeOfBirthWrapper}>
          <div className={cn.placeOfBirthRow}>
            <Controller
              control={control}
              name="birthCountryId"
              defaultValue=""
              render={({ field: { onChange, value, name } }) => (
                <Autocomplete
                  name={name}
                  placeholder="Select country..."
                  list={countries || []}
                  value={value}
                  className={`${commonStyles.newApplicationAutocomplete} ${
                    !activePermissions?.includes(
                      PermissionEnum.APPLICATIONS_CUSTOMERS_EDIT_ACTION,
                    ) ||
                    Boolean(
                      status === NewApplicationStatus.ACCEPTED ||
                      status === NewApplicationStatus.DECLINED,
                    )
                      ? commonStyles.newApplicationAutocompleteDisabled
                      : ''
                  }`}
                  error={errors?.birthCountryId?.message}
                  size={SelectSize.SMALL}
                  onChange={(e) => {
                    onChange(e);
                    clearErrors(['birthCountryId']);
                  }}
                  borderless
                  tabIndex={
                    !activePermissions?.includes(
                      PermissionEnum.APPLICATIONS_CUSTOMERS_EDIT_ACTION,
                    ) ||
                    Boolean(
                      status === NewApplicationStatus.ACCEPTED ||
                      status === NewApplicationStatus.DECLINED,
                    )
                      ? -1
                      : 0
                  }
                  disabled={
                    !activePermissions?.includes(
                      PermissionEnum.APPLICATIONS_CUSTOMERS_EDIT_ACTION,
                    ) ||
                    Boolean(
                      status === NewApplicationStatus.ACCEPTED ||
                      status === NewApplicationStatus.DECLINED,
                    )
                  }
                  fullWidth
                />
              )}
            />
          </div>
          <div className={cn.placeOfBirthRow}>
            <Controller
              control={control}
              name="placeOfBirth"
              defaultValue=""
              render={({ field: { onChange, value, name } }) => (
                <InputText
                  name={name}
                  placeholder="Enter city.."
                  value={value}
                  onChange={(e) => {
                    onChange(e);
                    clearErrors(['placeOfBirth']);
                  }}
                  size={InputSize.SMALL}
                  className="borderless"
                  error={errors?.placeOfBirth?.message}
                  tabIndex={
                    !activePermissions?.includes(
                      PermissionEnum.APPLICATIONS_CUSTOMERS_EDIT_ACTION,
                    ) ||
                    Boolean(
                      status === NewApplicationStatus.ACCEPTED ||
                      status === NewApplicationStatus.DECLINED,
                    )
                      ? -1
                      : 0
                  }
                  disabled={
                    !activePermissions?.includes(
                      PermissionEnum.APPLICATIONS_CUSTOMERS_EDIT_ACTION,
                    ) ||
                    Boolean(
                      status === NewApplicationStatus.ACCEPTED ||
                      status === NewApplicationStatus.DECLINED,
                    )
                  }
                />
              )}
            />
          </div>
        </div>
      </InfoRow>
    )}
    <InfoRow
      title="Nationality"
      id="nationalityId"
      className={cn.nationalityWrapper}
      style={{
        position: 'relative',
        zIndex: 18,
      }}
      lessPadding
    >
      <Controller
        control={control}
        name="nationalityId"
        defaultValue=""
        render={({ field: { onChange, value } }) => (
          <Autocomplete
            name="nationalityId"
            placeholder="Select country..."
            list={countries || []}
            value={value}
            className={`${commonStyles.newApplicationAutocomplete} ${
              status === NewApplicationStatus.ACCEPTED ||
              status === NewApplicationStatus.DECLINED
                ? commonStyles.newApplicationAutocompleteDisabled
                : ''
            }`}
            error={errors?.nationalityId?.message}
            size={SelectSize.SMALL}
            onChange={(e) => {
              onChange(e);
              clearErrors(['nationalityId']);
            }}
            borderless
            tabIndex={
              hasSumSubId ||
              !isEditable ||
              Boolean(
                status === NewApplicationStatus.ACCEPTED ||
                status === NewApplicationStatus.DECLINED,
              )
                ? -1
                : 0
            }
            disabled={
              !isEditable ||
              Boolean(
                status === NewApplicationStatus.ACCEPTED ||
                status === NewApplicationStatus.DECLINED,
              )
            }
            fullWidth
          />
        )}
      />
    </InfoRow>
    {(regulatedCompanyScheme || cofRegulatedCompanyScheme) && (
      <>
        {questionnarieIsLoading && (
          <div className={commonStyles.dictionaryLoader}>
            <IconLoader />
          </div>
        )}
        {!questionnarieIsLoading && (
          <>
            <InfoRow
              title="Other nationalities"
              id="otherNationalitiesIds"
              className={cn.nationalityWrapper}
              style={{
                position: 'relative',
                zIndex: 17,
              }}
              lessPadding
            >
              <Controller
                control={questionnarieControl}
                name="otherNationalitiesIds"
                defaultValue={[]}
                render={({ field: { onChange, value, name } }) => (
                  <Select
                    name={name}
                    placeholder="Select countries..."
                    list={countries || []}
                    value={value}
                    className={`${commonStyles.newApplicationAutocomplete} ${
                      !activePermissions?.includes(
                        PermissionEnum.APPLICATIONS_CUSTOMERS_EDIT_ACTION,
                      ) ||
                      Boolean(
                        status === NewApplicationStatus.ACCEPTED ||
                        status === NewApplicationStatus.DECLINED,
                      )
                        ? commonStyles.newApplicationAutocompleteDisabled
                        : ''
                    }`}
                    error={questionnarieErrors?.otherNationalitiesIds?.message}
                    size={SelectSize.SMALL}
                    onChange={(e) => {
                      onChange(e);
                      questionnarieClearErrors(['otherNationalitiesIds']);

                      if (!(e.target.value as string[])?.length) {
                        questionnarieClearErrors([
                          'secondPassportIssueCountryId',
                        ]);
                        questionnarieClearErrors(['secondPassportIssueDate']);
                        questionnarieClearErrors(['secondPassportNumber']);
                      }
                    }}
                    borderless
                    tabIndex={
                      !activePermissions?.includes(
                        PermissionEnum.APPLICATIONS_CUSTOMERS_EDIT_ACTION,
                      ) ||
                      Boolean(
                        status === NewApplicationStatus.ACCEPTED ||
                        status === NewApplicationStatus.DECLINED,
                      )
                        ? -1
                        : 0
                    }
                    disabled={
                      !activePermissions?.includes(
                        PermissionEnum.APPLICATIONS_CUSTOMERS_EDIT_ACTION,
                      ) ||
                      Boolean(
                        status === NewApplicationStatus.ACCEPTED ||
                        status === NewApplicationStatus.DECLINED,
                      )
                    }
                    mode={SelectType.MULTIPLE}
                    fullWidth
                  />
                )}
              />
            </InfoRow>
            <InfoRow
              title="Marital Status"
              id="maritalStatus"
              className={cn.nationalityWrapper}
              lessPadding
            >
              <Controller
                control={questionnarieControl}
                name="maritalStatus"
                defaultValue=""
                render={({ field: { onChange, value, name } }) => (
                  <Select
                    name={name}
                    placeholder="Select status..."
                    list={[
                      {
                        name: 'Single',
                        value: 'SINGLE',
                      },
                      {
                        name: 'Divorced',
                        value: 'DIVORCED',
                      },
                      {
                        name: 'Married',
                        value: 'MARRIED',
                      },
                    ]}
                    value={value}
                    className={`${commonStyles.newApplicationAutocomplete} ${
                      !activePermissions?.includes(
                        PermissionEnum.APPLICATIONS_CUSTOMERS_EDIT_ACTION,
                      ) ||
                      Boolean(
                        status === NewApplicationStatus.ACCEPTED ||
                        status === NewApplicationStatus.DECLINED,
                      )
                        ? commonStyles.newApplicationAutocompleteDisabled
                        : ''
                    }`}
                    error={questionnarieErrors?.maritalStatus?.message}
                    size={SelectSize.SMALL}
                    onChange={(e) => {
                      onChange(e);
                      questionnarieClearErrors(['maritalStatus']);
                    }}
                    borderless
                    tabIndex={
                      !activePermissions?.includes(
                        PermissionEnum.APPLICATIONS_CUSTOMERS_EDIT_ACTION,
                      ) ||
                      Boolean(
                        status === NewApplicationStatus.ACCEPTED ||
                        status === NewApplicationStatus.DECLINED,
                      )
                        ? -1
                        : 0
                    }
                    disabled={
                      !activePermissions?.includes(
                        PermissionEnum.APPLICATIONS_CUSTOMERS_EDIT_ACTION,
                      ) ||
                      Boolean(
                        status === NewApplicationStatus.ACCEPTED ||
                        status === NewApplicationStatus.DECLINED,
                      )
                    }
                    fullWidth
                  />
                )}
              />
            </InfoRow>
            {questionnarieFields?.maritalStatus === 'MARRIED' && (
              <InfoRow title="Spouse full name" id="spouseFullName" lessPadding>
                <Controller
                  control={questionnarieControl}
                  name="spouseFullName"
                  defaultValue=""
                  render={({ field: { onChange, value, name } }) => (
                    <InputText
                      name={name}
                      placeholder="Enter full name.."
                      value={value}
                      onChange={(e) => {
                        onChange(e);
                        questionnarieClearErrors(['spouseFullName']);
                      }}
                      size={InputSize.SMALL}
                      className="borderless"
                      error={questionnarieErrors?.spouseFullName?.message}
                      tabIndex={
                        !activePermissions?.includes(
                          PermissionEnum.APPLICATIONS_CUSTOMERS_EDIT_ACTION,
                        ) ||
                        Boolean(
                          status === NewApplicationStatus.ACCEPTED ||
                          status === NewApplicationStatus.DECLINED,
                        )
                          ? -1
                          : 0
                      }
                      disabled={
                        !activePermissions?.includes(
                          PermissionEnum.APPLICATIONS_CUSTOMERS_EDIT_ACTION,
                        ) ||
                        Boolean(
                          status === NewApplicationStatus.ACCEPTED ||
                          status === NewApplicationStatus.DECLINED,
                        )
                      }
                    />
                  )}
                />
              </InfoRow>
            )}
          </>
        )}
      </>
    )}

    {applicationScheme && applicationScheme?.taxNumberActive && (
      <InfoRow title="TAX Number" id="taxNumber" lessPadding>
        <Controller
          control={control}
          name="taxNumber"
          defaultValue=""
          render={({ field: { onChange, value } }) => (
            <InputText
              name="taxNumber"
              placeholder="Enter TAX..."
              value={value}
              onChange={(e) => {
                onChange(e);
                clearErrors(['taxNumber']);
              }}
              size={InputSize.SMALL}
              error={errors?.taxNumber?.message}
              className="borderless"
              tabIndex={
                !activePermissions?.includes(
                  PermissionEnum.APPLICATIONS_CUSTOMERS_EDIT_ACTION,
                ) ||
                Boolean(
                  status === NewApplicationStatus.ACCEPTED ||
                  status === NewApplicationStatus.DECLINED,
                )
                  ? -1
                  : 0
              }
              disabled={
                !activePermissions?.includes(
                  PermissionEnum.APPLICATIONS_CUSTOMERS_EDIT_ACTION,
                ) ||
                Boolean(
                  status === NewApplicationStatus.ACCEPTED ||
                  status === NewApplicationStatus.DECLINED,
                )
              }
            />
          )}
        />
      </InfoRow>
    )}
  </StepPartBlock>
);

export default GeneralInfo;
